import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { LightTheme, useTheme } from "../../hooks/useTheme";
import { detectedCensored, truncateString } from "@markit/common.utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { invalidEndDate } from "../../utils/makeEvent";
import ContentViolationTagModal from "../DeliveryScore/ContentViolationTagModal";
import { Event, EventType } from "@markit/common.types";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import CircularProgress from "@mui/material/CircularProgress";
import CreateDescriptionModal from "./CreateModals/CreateDescriptionModal";
import CreateLocationModal from "./CreateModals/CreateLocationModal";
import { generateTextFromJSON } from "../../utils/stringUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import TimezoneSelect from "react-timezone-select";
import { API } from "../../API";

// Configure dayjs timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

type CreateEventTitleDescriptionFormProps = {
  eventSettings: Event;
  updateEventSettings: (update: Partial<Event>) => void;
  showError: boolean;
  isEditing: boolean;
};

const CreateEventTitleDescriptionForm = (
  props: CreateEventTitleDescriptionFormProps
) => {
  const { eventSettings, updateEventSettings, showError, isEditing } = props;
  const { theme } = useTheme(eventSettings.theme);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isEventDescriptionModalOpen, setIsEventDescriptionModalOpen] =
    useState(false);
  const [locationAddress, setLocationAddress] = useState<string>("");
  const [generatingTitle, setGeneratingTitle] = useState(false);

  const styles = {
    sectionContainer: {
      padding: 14,
      borderRadius: 12,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      ...theme.NewPrimaryBG,
    },
  };

  const eventDescription = useMemo(
    () => generateTextFromJSON(eventSettings.body),
    [eventSettings.body]
  );

  useEffect(() => {
    if (isEditing) {
      if (locationAddress === "") {
        setLocationAddress(eventSettings.googleDescription);
      }
      // Default to system timezone for old events without a timezone
      if (!eventSettings.timezone) {
        updateEventSettings({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
    }
  }, [
    eventSettings.customTickets,
    eventSettings.googleDescription,
    eventSettings.timezone,
    isEditing,
    locationAddress,
    updateEventSettings,
  ]);

  const addressLabel = useMemo(() => {
    return !eventSettings.isVirtual && eventSettings.formattedAddress !== ""
      ? eventSettings.formattedAddress
      : eventSettings.isVirtual && eventSettings.locationDetails
        ? eventSettings.locationDetails
        : undefined;
  }, [
    eventSettings.formattedAddress,
    eventSettings.isVirtual,
    eventSettings.locationDetails,
  ]);

  // Ensure we always have a valid timezone for date pickers
  const validTimezone = useMemo(() => {
    if (!eventSettings.timezone || eventSettings.timezone === "") {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        return "UTC";
      }
    }
    return eventSettings.timezone;
  }, [eventSettings.timezone]);

  const onChangeStartDate = useCallback(
    (newValue) => {
      // newValue is a dayjs object in the selected timezone (due to timezone prop on picker)
      // We need to convert it from the selected timezone to UTC for storage
      const dateInTimezone = dayjs(newValue).tz(validTimezone);
      const utcDate = dateInTimezone.utc().toDate();

      // Calculate the difference in milliseconds to maintain the duration
      const oldStart = new Date(eventSettings.start).getTime();
      const newStart = utcDate.getTime();
      const difference = newStart - oldStart;
      const newEndDate = new Date(
        new Date(eventSettings.end).getTime() + difference
      );

      updateEventSettings({ start: utcDate.toISOString() });
      updateEventSettings({ end: newEndDate.toISOString() });
    },
    [eventSettings.end, eventSettings.start, updateEventSettings, validTimezone]
  );

  const onChangeEndDate = useCallback(
    (newValue) => {
      // newValue is a dayjs object in the selected timezone
      // Convert from selected timezone to UTC for storage
      const dateInTimezone = dayjs(newValue).tz(validTimezone);
      const utcDate = dateInTimezone.utc().toDate();
      updateEventSettings({ end: utcDate.toISOString() });
    },
    [updateEventSettings, validTimezone]
  );

  const handleTimezoneChange = useCallback(
    (timezone: any) => {
      // react-timezone-select returns an object with { value, label, abbrev, altName, offset }
      const tzValue =
        typeof timezone === "object" && timezone !== null && "value" in timezone
          ? timezone.value
          : timezone;
      if (typeof tzValue === "string") {
        const oldTimezone = eventSettings.timezone;
        const newTimezone = tzValue;

        // If timezone is actually changing (not just initializing), maintain the same local time
        if (oldTimezone && oldTimezone !== newTimezone && eventSettings.start) {
          // Get the current local time components in the old timezone
          const startInOldTz = dayjs(eventSettings.start).tz(oldTimezone);
          const endInOldTz = dayjs(eventSettings.end).tz(oldTimezone);

          // Extract the date/time components and create the same local time in the new timezone
          // This maintains "3:00 PM EST" -> "3:00 PM PST" (not "12:00 PM PST")
          const startLocalString = startInOldTz.format("YYYY-MM-DD HH:mm:ss");
          const endLocalString = endInOldTz.format("YYYY-MM-DD HH:mm:ss");

          // Parse the local time string as if it's in the new timezone
          const startInNewTz = dayjs.tz(startLocalString, newTimezone);
          const endInNewTz = dayjs.tz(endLocalString, newTimezone);

          // Convert to UTC for storage
          const newStartUTC = startInNewTz.utc().toDate().toISOString();
          const newEndUTC = endInNewTz.utc().toDate().toISOString();

          updateEventSettings({
            timezone: newTimezone,
            start: newStartUTC,
            end: newEndUTC,
          });
        } else {
          updateEventSettings({ timezone: tzValue });
        }
      }
    },
    [
      eventSettings.start,
      eventSettings.end,
      eventSettings.timezone,
      updateEventSettings,
    ]
  );

  const onGenerateTitle = useCallback(async () => {
    setGeneratingTitle(true);
    try {
      const { title } = await API.events.generateTitle({
        event: { ...eventSettings, description: eventDescription },
      });
      if (title) {
        updateEventSettings({ title });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingTitle(false);
    }
  }, [eventDescription, eventSettings, updateEventSettings]);

  const renderEventDescriptionModal = useCallback(() => {
    const closeModal = () => {
      setIsEventDescriptionModalOpen(false);
    };

    return (
      <CreateDescriptionModal
        eventSettings={eventSettings}
        updateEventSettings={updateEventSettings}
        title={"Event Description"}
        maxLength={4000}
        onClose={closeModal}
      />
    );
  }, [eventSettings, updateEventSettings]);

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <input
              className={
                showError && eventSettings.title === ""
                  ? "titleInputError"
                  : "titleInput"
              }
              style={{
                flex: 1,
                minWidth: 0,
                color: theme.PrimaryText.color,
                fontStyle:
                  showError && eventSettings.title === "" ? "italic" : "",
              }}
              type="text"
              maxLength={100}
              value={eventSettings.title}
              placeholder={"Event Name..."}
              onChange={(e) => updateEventSettings({ title: e.target.value })}
            />
            <div
              className="Centering"
              style={{
                flexShrink: 0,
                pointerEvents: generatingTitle ? "none" : "auto",
                cursor: "pointer",
              }}
              onClick={onGenerateTitle}
            >
              {generatingTitle ? (
                <CircularProgress size={20} style={{ color: Colors.BLUE5 }} />
              ) : (
                <Icon
                  icon={"ion:sparkles"}
                  height={20}
                  style={{ color: Colors.BLUE5 }}
                />
              )}
            </div>
          </div>
          {eventSettings.title.length === 100 ||
          detectedCensored(eventSettings.title) ? (
            <div
              className="AlignedRow"
              style={{ gap: 7, justifyContent: "flex-end" }}
            >
              {eventSettings.title.length === 100 ? (
                <span
                  className="smallBodySubtext"
                  style={{ color: Colors.GRAY2 }}
                >
                  {eventSettings.title.length}/100
                </span>
              ) : null}
              {detectedCensored(eventSettings.title) ? (
                <ContentViolationTagModal
                  message={eventSettings.title}
                  numCredits={0}
                  theme={theme}
                  alternateTitle="Content Violation"
                />
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="RowNormal" style={{ gap: 14 }}>
          <div>
            <Icon
              icon={"ion:calendar"}
              height={25}
              style={{ color: Colors.RED2 }}
            />
          </div>
          <StandardBorderedContainer
            className="RowNormal"
            containerStyles={styles.sectionContainer}
          >
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="dateWrapper">
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  Start
                </span>
                <div className="dateContainer" style={theme.SecondaryBG}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: {
                          style: { ...theme.PrimaryText },
                        },
                        sx: {
                          "& fieldset": {
                            border: "none",
                          },
                          "& .MuiButtonBase-root": {
                            color:
                              theme === LightTheme
                                ? Colors.GRAY3
                                : Colors.WHITE,
                          },
                        },
                      },
                    }}
                    disablePast
                    timezone={validTimezone}
                    value={dayjs(new Date(eventSettings.start).getTime())}
                    onChange={onChangeStartDate}
                  />
                  <TimePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: {
                          style: { ...theme.PrimaryText },
                        },
                        sx: {
                          "& fieldset": {
                            border: "none",
                            borderLeft: "0.5px solid #B9B9B9",
                            borderRadius: "0",
                          },
                          "& .MuiButtonBase-root": {
                            color:
                              theme === LightTheme
                                ? Colors.GRAY3
                                : Colors.WHITE,
                          },
                        },
                      },
                    }}
                    timezone={validTimezone}
                    value={dayjs(new Date(eventSettings.start).getTime())}
                    onChange={onChangeStartDate}
                  />
                </div>
              </div>
              <div className="dateWrapper">
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  End
                </span>
                <div className="dateContainer" style={theme.SecondaryBG}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: {
                          style: { ...theme.PrimaryText },
                        },
                        sx: {
                          "& fieldset": {
                            border: invalidEndDate(eventSettings)
                              ? "1px solid #DF4840"
                              : "none",
                            borderColor: Colors.RED1,
                          },
                          "& .MuiButtonBase-root": {
                            color:
                              theme === LightTheme
                                ? Colors.GRAY3
                                : Colors.WHITE,
                          },
                        },
                      },
                    }}
                    disablePast
                    timezone={validTimezone}
                    value={dayjs(new Date(eventSettings.end).getTime())}
                    onChange={onChangeEndDate}
                  />
                  <TimePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: {
                          style: { ...theme.PrimaryText },
                        },
                        sx: {
                          "& fieldset": {
                            border: invalidEndDate(eventSettings)
                              ? "1px solid #DF4840"
                              : "none",
                            borderLeft: invalidEndDate(eventSettings)
                              ? "1px solid #DF4840"
                              : "0.5px solid #B9B9B9",
                            borderRadius: "0",
                          },
                          "& .MuiButtonBase-root": {
                            color:
                              theme === LightTheme
                                ? Colors.GRAY3
                                : Colors.WHITE,
                          },
                        },
                      },
                    }}
                    timezone={validTimezone}
                    value={dayjs(new Date(eventSettings.end).getTime())}
                    onChange={onChangeEndDate}
                  />
                </div>
              </div>
              <div className="dateWrapper">
                <div className="AlignedRow">
                  <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                    Timezone
                  </span>
                </div>
                <TimezoneSelect
                  value={eventSettings.timezone}
                  onChange={handleTimezoneChange}
                  labelStyle="original"
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      backgroundColor: theme.SecondaryBG.backgroundColor,
                      borderRadius: "8px",
                      boxShadow: "none",
                      border: `0.5px solid ${Colors.GRAY2}`,
                      "&:hover": {
                        border: `0.5px solid ${Colors.GRAY2}`,
                      },
                      minHeight: "40px",
                      cursor: "pointer",
                    }),
                    singleValue: (base: any) => ({
                      ...base,
                      color: theme.PrimaryText.color,
                      fontSize: 14,
                      fontWeight: 400,
                    }),
                    input: (base: any) => ({
                      ...base,
                      color: theme.PrimaryText.color,
                      fontSize: 14,
                    }),
                    placeholder: (base: any) => ({
                      ...base,
                      color: theme.PrimaryText.color,
                      fontSize: 14,
                    }),
                    menu: (base: any) => ({
                      ...base,
                      borderRadius: 12,
                      boxShadow: "0px 24px 62px 2px rgba(0, 0, 0, 0.27)",
                      zIndex: 9999,
                    }),
                    menuList: (base: any) => ({
                      ...base,
                      padding: 0,
                      maxHeight: 400,
                      borderRadius: 12,
                    }),
                    option: (base: any, state: any) => ({
                      ...base,
                      backgroundColor: state.isFocused
                        ? theme.NewPrimaryBG.backgroundColor
                        : theme.SecondaryBG.backgroundColor,
                      color: theme.PrimaryText.color,
                      fontSize: 14,
                      "&:active": {
                        opacity: 0.8,
                        backgroundColor: theme.SecondaryBG.backgroundColor,
                      },
                      cursor: "pointer",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                    dropdownIndicator: (base: any) => ({
                      ...base,
                      color: theme === LightTheme ? Colors.GRAY3 : Colors.WHITE,
                      "&:hover": {
                        color:
                          theme === LightTheme ? Colors.GRAY3 : Colors.WHITE,
                      },
                    }),
                  }}
                />
              </div>
            </div>
          </StandardBorderedContainer>
        </div>
        <div className="AlignedRowSelect" style={{ gap: 14 }}>
          <div>
            <Icon
              icon={"mdi:map-marker"}
              height={25}
              style={{ color: Colors.BLUE5 }}
            />
          </div>
          <StandardBorderedContainer
            className="RowNormal"
            containerStyles={{
              ...styles.sectionContainer,
              flex: 1,
              border:
                showError &&
                !eventSettings.formattedAddress &&
                !eventSettings.isVirtual
                  ? "0.5px solid #DF4840"
                  : `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
            }}
            onPress={() => setIsLocationModalOpen(true)}
          >
            <div className="AlignedRowSpaced" style={{ flex: 1 }}>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  {eventSettings.isVirtual
                    ? eventSettings.locationDetails
                      ? "Virtual Event"
                      : "Add Webinar Link"
                    : eventSettings.googleDescription !== ""
                      ? truncateString(
                          eventSettings.googleDescription,
                          54,
                          "..."
                        )
                      : "Add Location"}
                </span>
                {addressLabel ? (
                  <span
                    className="smallBodySubtext"
                    style={{
                      overflowWrap: "break-word",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {addressLabel}
                  </span>
                ) : null}
              </div>
              <Icon
                icon={"ion:create"}
                height={18}
                style={{ color: theme.PrimaryText.color }}
              />
            </div>
          </StandardBorderedContainer>
        </div>
        {eventSettings.eventType === EventType.MARKIT ||
        eventSettings.eventType === EventType.WEBINAR ? (
          <div className="AlignedRowSelect" style={{ gap: 14 }}>
            <div>
              <Icon icon={"ion:text"} height={25} style={theme.PrimaryText} />
            </div>
            <StandardBorderedContainer
              className="RowNormal"
              containerStyles={{ ...styles.sectionContainer, flex: 1 }}
              onPress={() => setIsEventDescriptionModalOpen(true)}
            >
              <div className="AlignedRowSpaced" style={{ gap: 24 }}>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                    Description
                  </span>
                  {eventDescription ? (
                    <span
                      className="TextOverflowEllipsis"
                      style={{
                        color: Colors.GRAY2,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                        maxWidth: "380px",
                      }}
                    >
                      {eventDescription}
                    </span>
                  ) : null}
                </div>
                <div>
                  <Icon
                    icon={"ion:create"}
                    height={18}
                    style={{
                      color: theme.PrimaryText.color,
                    }}
                  />
                </div>
              </div>
            </StandardBorderedContainer>
          </div>
        ) : null}
      </div>
      {/* Modal for setting event description and texts */}
      {isEventDescriptionModalOpen ? renderEventDescriptionModal() : null}
      {/* Modal for setting location */}
      {isLocationModalOpen ? (
        <CreateLocationModal
          eventSettings={eventSettings}
          updateEventSettings={updateEventSettings}
          setLocationAddress={setLocationAddress}
          setIsLocationModalOpen={setIsLocationModalOpen}
        />
      ) : null}
    </>
  );
};

export default memo(CreateEventTitleDescriptionForm);
